import { Button, Input } from "antd";
import queryClient from "api/queryClient.ts";
import {
    useGetIncomingPostbackMethodsExportQuery,
    useUpdateIncomingPostbackMethodsMutation,
} from "api/TenantSlug/IncomingPostback/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, ModifyIncomingPostbackMethodsFormData } from "./formSchema";

import IncomingPostbackDataOptional = App.Data.IncomingPostbackDataOptional;
import StoreIncomingPostbackConnectionSequenceStepsRequest = App.Http.Requests.Tracking.StoreIncomingPostbackConnectionSequenceStepsRequest;

interface ApiToolGeneralFormProps {
    incomingPostback: IncomingPostbackDataOptional;
    onClose: () => void;
}

const ModifyPostbackIncomingMethodsFormContent: FC<ApiToolGeneralFormProps> = ({ incomingPostback, onClose }) => {
    const { t } = usePaldockTranslation("forms.modify-postback-incoming-methods");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ModifyIncomingPostbackMethodsFormData>();
    const { control, handleSubmit, fieldNames } = generalFormUtils;
    const permissionsUtils = usePermission();

    const updateIncomingPostbackMethodsMutation = useUpdateIncomingPostbackMethodsMutation(incomingPostback.id ?? "");

    const onSubmitForm = (data: ModifyIncomingPostbackMethodsFormData) => {
        const payload: StoreIncomingPostbackConnectionSequenceStepsRequest = {
            connectionSequenceSteps: JSON.parse(data.connectionSequenceSteps),
        };

        updateIncomingPostbackMethodsMutation.mutate(payload, {
            onSuccess: () => {
                toast.success(t("messages.update_success"));
                queryClient.refetchQueries({ queryKey: ["getIncomingPostbackMethodsExport", incomingPostback.id] });
                queryClient.refetchQueries({ queryKey: ["getConnectionSequenceSteps"] });
                onClose();
            },
            onError: () => {
                toast.error(t("messages.update_error"));
                control._resetDefaultValues;
            },
        });
    };

    return (
        <>
            <FormItem
                control={control}
                name={fieldNames.connectionSequenceSteps?.name || "connectionSequenceSteps"}
                label={t("fields.connection_sequence_steps.label")}
            >
                <Input.TextArea rows={4} placeholder={t("fields.connection_sequence_steps.placeholder")} />
            </FormItem>
            {/* <FormItem control={control} name={fieldNames.integration?.name || "integration"} label="Integration"> // todo edit via ace editor
                <CodeEditor id={fieldNames.integration?.name}>{control._defaultValues.integration}</CodeEditor>
            </FormItem>*/}
            {permissionsUtils.hasPermission(PermissionEnum.ToolTrackingIncomingPostbackEditorExportSave) && (
                <Button type="primary" onClick={handleSubmit(onSubmitForm)}>
                    {tC("save")}
                </Button>
            )}
        </>
    );
};

export const ModifyPostbackIncomingMethodsForm: FC<ApiToolGeneralFormProps> = ({ incomingPostback, onClose }) => {
    const getIncomingPostbackMethodsExportQuery = useGetIncomingPostbackMethodsExportQuery(incomingPostback?.id || "");
    const data = getIncomingPostbackMethodsExportQuery.data;

    if (!incomingPostback || !data) return null;

    const defaultConnectionSequenceSteps = data.connectionSequenceSteps.map((step) => ({
        ...step,
        requestConfig: { ...step.requestConfig, name: "config" },
    }));

    return (
        <GeneralFormProvider<ModifyIncomingPostbackMethodsFormData>
            schema={formSchema}
            id={FormId.TrackingToolFromMethodsExportForm}
            defaultValues={{
                connectionSequenceSteps: JSON.stringify(defaultConnectionSequenceSteps),
            }}
        >
            <ModifyPostbackIncomingMethodsFormContent incomingPostback={incomingPostback} onClose={onClose} />
        </GeneralFormProvider>
    );
};
